import React, { useEffect, useState } from 'react';
import { paroissesService, diocesesService } from '../services/api';
import { FiPlus, FiEdit, FiTrash } from 'react-icons/fi';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { useForm } from 'react-hook-form';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';
import Select from 'react-select';
import { useTheme } from '../contexts/ThemeContext';
import { useMemo } from 'react';

const ParoissesPage = () => {
  const [paroisses, setParoisses] = useState([]);
  const [dioceses, setDioceses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingParoisse, setEditingParoisse] = useState(null);
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm();
  const { theme } = useTheme();

  const selectStyles = useMemo(() => ({
    control: (base) => ({
      ...base,
      backgroundColor: theme === 'dark' ? '#374151' : '#fff',
      borderColor: theme === 'dark' ? '#4B5563' : '#D1D5DB',
      color: theme === 'dark' ? '#E5E7EB' : '#111827',
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: theme === 'dark' ? '#374151' : '#fff',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused 
        ? (theme === 'dark' ? '#4B5563' : '#E5E7EB')
        : (theme === 'dark' ? '#374151' : '#fff'),
      color: theme === 'dark' ? '#E5E7EB' : '#111827',
    }),
    singleValue: (base) => ({
      ...base,
      color: theme === 'dark' ? '#E5E7EB' : '#111827',
    }),
    input: (base) => ({
      ...base,
      color: theme === 'dark' ? '#E5E7EB' : '#111827',
    }),
  }), [theme]);

  const fetchParoisses = async () => {
    const res = await paroissesService.getAll();
    setParoisses(res.data.data || []);
  };

  const fetchDioceses = async () => {
    const res = await diocesesService.getAll();
    setDioceses(res.data.data || []);
  };

  useEffect(() => {
    fetchParoisses();
    fetchDioceses();
  }, []);

  const dioceseOptions = dioceses.map(d => ({ value: d.id, label: d.diocese }));

  const onSubmit = async (data) => {
    try {
      if (editingParoisse) {
        await paroissesService.update(editingParoisse.id, data);
        Swal.fire('Succès', 'Paroisse modifiée avec succès', 'success');
      } else {
        await paroissesService.create(data);
        Swal.fire('Succès', 'Paroisse ajoutée avec succès', 'success');
      }
      setShowModal(false);
      setEditingParoisse(null);
      reset();
      fetchParoisses();
    } catch (error) {
      Swal.fire('Erreur', error.response?.data?.message || 'Erreur lors de l\'enregistrement', 'error');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Paroisses</h1>
        <ConditionalButton
          entity="paroisses"
          permission="create"
          className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          onClick={() => {
            setEditingParoisse(null);
            reset();
            setShowModal(true);
          }}
        >
          <FiPlus />
          <span>Nouvelle paroisse</span>
        </ConditionalButton>
      </div>
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Diocèse</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Paroisse</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {paroisses.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">Aucune paroisse</td>
              </tr>
            ) : (
              paroisses.map((paroisse) => (
                <tr key={paroisse.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{paroisse.diocese_nom || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">{paroisse.paroisse}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <ConditionalActions
                      entity="paroisses"
                      onEdit={() => {
                        setEditingParoisse(paroisse);
                        setShowModal(true);
                        setValue('paroisse', paroisse.paroisse);
                        setValue('diocese_id', paroisse.diocese_id);
                      }}
                      onDelete={async () => {
                        const result = await Swal.fire({
                          title: 'Supprimer cette paroisse ?',
                          text: 'Cette action est irréversible.',
                          icon: 'warning',
                          showCancelButton: true,
                          confirmButtonColor: '#d33',
                          cancelButtonColor: '#3085d6',
                          confirmButtonText: 'Oui, supprimer',
                          cancelButtonText: 'Annuler',
                        });
                        if (result.isConfirmed) {
                          try {
                            await paroissesService.delete(paroisse.id);
                            fetchParoisses();
                            Swal.fire('Supprimé !', 'La paroisse a été supprimée.', 'success');
                          } catch (error) {
                            Swal.fire('Erreur', error.response?.data?.message || 'Erreur lors de la suppression', 'error');
                          }
                        }
                      }}
                      editIcon={FiEdit}
                      deleteIcon={FiTrash}
                      className="flex space-x-2 justify-end"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">{editingParoisse ? 'Modifier' : 'Ajouter une'} paroisse</h2>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Diocèse *</label>
                <Select
                  options={dioceseOptions}
                  value={dioceseOptions.find(opt => opt.value === watch('diocese_id')) || null}
                  onChange={opt => setValue('diocese_id', opt ? opt.value : '', { shouldValidate: true })}
                  isClearable
                  placeholder="Sélectionner un diocèse"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
                {errors.diocese_id && <span className="text-red-500 dark:text-red-400 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Paroisse *</label>
                <input {...register('paroisse', { required: true })} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" />
                {errors.paroisse && <span className="text-red-500 dark:text-red-400 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div className="flex justify-end space-x-2">
                <button type="button" className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600" onClick={() => { setShowModal(false); setEditingParoisse(null); reset(); }}>Annuler</button>
                <ConditionalButton
                  entity="paroisses"
                  permission={editingParoisse ? "update" : "create"}
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Enregistrer
                </ConditionalButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParoissesPage;

